import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ProfileIconWithFallback from '../../shared/ProfileIcon';
import ChampionIconWithFallback from '../../shared/ChampionIcon';
import {
    buildSearchChampionIconSrc,
    buildSearchProfileIconSrc,
    buildSearchTierIconSrc,
    buildSearchRoleIconSrc,
    formatSearchEloLabel,
} from './searchHelpers';

const SearchSuggestionDropdown = ({
    anchorRef,
    championResults,
    hasSelectedRegion,
    loading,
    query,
    recentSearches,
    results,
    onSelectRecentSearch,
    onSelectChampionAnalytics,
    onSelectChampionPlayers,
    onSelectSummoner,
    onSubmitDirectSearch,
    onViewAllResults,
}) => {
    const [portalStyle, setPortalStyle] = useState(null);
    const trimmedQuery = query.trim();
    const canSearchDirectly = trimmedQuery.includes('#') && trimmedQuery.split('#')[0]?.trim() && trimmedQuery.split('#')[1]?.trim();
    const hasExactCachedMatch = results.some((result) => result.exact_match);
    const hasChampionMatches = championResults.length > 0;
    const hasPlayerMatches = results.length > 0;
    const hasRecentSearches = recentSearches.length > 0;

    useEffect(() => {
        if (typeof window === 'undefined' || (!trimmedQuery && !hasRecentSearches)) {
            setPortalStyle(null);
            return undefined;
        }

        function updatePosition() {
            const anchorElement = anchorRef?.current;
            if (!anchorElement) {
                return;
            }

            const rect = anchorElement.getBoundingClientRect();
            const horizontalMargin = 12;
            const width = Math.min(rect.width, window.innerWidth - horizontalMargin * 2);
            const left = Math.min(
                Math.max(horizontalMargin, rect.left),
                window.innerWidth - width - horizontalMargin,
            );
            const availableHeight = Math.max(180, window.innerHeight - rect.bottom - 22);

            setPortalStyle({
                left,
                top: rect.bottom + 10,
                width,
                maxHeight: availableHeight,
            });
        }

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [anchorRef, trimmedQuery, championResults.length, results.length, loading, hasSelectedRegion, hasRecentSearches]);

    if (!trimmedQuery && !hasRecentSearches) {
        return null;
    }

    const dropdownContent = (
        <div
            data-search-suggestion-dropdown="true"
            className="fixed z-[1600] isolate flex flex-col overflow-hidden rounded-2xl border border-cyan-900/55 bg-zinc-950 shadow-[0_18px_60px_rgba(0,0,0,0.58)]"
            style={portalStyle}
        >
            {!trimmedQuery && hasRecentSearches ? (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    <div className="border-b border-cyan-950/70 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                        Recent Searches
                    </div>
                    {recentSearches.map((recentSearch, index) => (
                        <button
                            key={`${recentSearch.kind}-${recentSearch.label}-${recentSearch.displayRegion}-${index}`}
                            type="button"
                            className="flex w-full items-center justify-between gap-4 border-b border-cyan-950/55 px-4 py-3 text-left transition duration-150 hover:bg-cyan-950/28"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => onSelectRecentSearch(recentSearch)}
                        >
                            <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-zinc-100">
                                    {recentSearch.label}
                                </div>
                                <div className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                    {recentSearch.displayRegion}
                                </div>
                            </div>
                            <div className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
                                {recentSearch.kind === 'summoner'
                                    ? 'Player'
                                    : recentSearch.kind === 'champion-analytics'
                                        ? 'Analytics'
                                    : recentSearch.kind === 'champion'
                                        ? 'Players'
                                        : 'Search'}
                            </div>
                        </button>
                    ))}
                </div>
            ) : !hasSelectedRegion ? (
                <div className="px-4 py-3 text-sm text-zinc-300">
                    Select a region to search for summoners.
                </div>
            ) : loading ? (
                <div className="px-4 py-3 text-sm text-zinc-300">
                    Searching…
                </div>
            ) : hasChampionMatches || hasPlayerMatches ? (
                <>
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        {hasChampionMatches ? (
                            <div className="border-b border-cyan-950/55 px-4 py-3">
                                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                    Champions
                                </div>
                                    <div className="flex flex-wrap gap-2">
                                        {championResults.map((champion) => (
                                            <React.Fragment key={champion.champion_id}>
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-2 rounded-full border border-cyan-950/55 bg-zinc-900 py-1.5 pl-1 pr-3 text-left transition duration-150 hover:bg-cyan-950/28"
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => onSelectChampionAnalytics(champion)}
                                            >
                                                <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-cyan-950/55">
                                                <ChampionIconWithFallback
                                                    className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                    src={buildSearchChampionIconSrc(champion.name_id)}
                                                    alt={`${champion.name} icon`}
                                                />
                                                </div>
                                                <span className="text-[12px] font-semibold text-zinc-200">{champion.name} Analytics</span>
                                            </button>
                                            <button
                                                key={`${champion.champion_id}-players`}
                                                type="button"
                                                className="inline-flex items-center gap-2 rounded-full border border-cyan-950/55 bg-zinc-900 py-1.5 pl-1 pr-3 text-left transition duration-150 hover:bg-cyan-950/28"
                                                onMouseDown={(event) => event.preventDefault()}
                                                onClick={() => onSelectChampionPlayers(champion)}
                                            >
                                                <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-cyan-950/55">
                                                    <ChampionIconWithFallback
                                                        className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                        src={buildSearchChampionIconSrc(champion.name_id)}
                                                        alt={`${champion.name} icon`}
                                                    />
                                                </div>
                                                <span className="text-[12px] font-semibold text-zinc-200">{champion.name} Players</span>
                                            </button>
                                            </React.Fragment>
                                        ))}
                                    </div>
                            </div>
                        ) : null}

                        {hasPlayerMatches ? (
                            <div className="border-b border-cyan-950/70 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                Previously Searched Players
                            </div>
                        ) : null}

                        {results.map((result) => {
                            const eloLabel = formatSearchEloLabel(result.competitive);
                            const isRanked = Boolean(result.competitive?.tier && result.competitive.tier !== 'UNRANKED');
                            const masteryEntries = (result.champion_masteries || []).slice(0, 3);
                            const primaryMastery = masteryEntries[0];

                            return (
                                <button
                                    key={`${result.puuid}-${result.platform_code}`}
                                    type="button"
                                    className="grid w-full gap-3 border-b border-cyan-950/55 px-4 py-3 text-left transition duration-150 hover:bg-cyan-950/28 md:grid-cols-[228px_288px_136px] md:items-center md:justify-between md:gap-4"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => onSelectSummoner(result)}
                                >
                                    <div className="md:hidden">
                                        <div className="grid grid-cols-[minmax(0,1fr)_36px_52px] items-center gap-3">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                                    <ProfileIconWithFallback
                                                        className="block h-full w-full object-cover"
                                                        src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                        alt="summoner profile icon"
                                                    />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex min-w-0 items-center gap-1.5">
                                                        <div className="truncate text-sm font-semibold text-zinc-100">
                                                            {result.game_name}
                                                        </div>
                                                        {result.preferred_role ? (
                                                            <img
                                                                alt={`${result.preferred_role} role icon`}
                                                                className="h-3.5 w-3.5 shrink-0 opacity-90"
                                                                src={buildSearchRoleIconSrc(result.preferred_role)}
                                                            />
                                                        ) : null}
                                                    </div>
                                                    <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                        #{result.tag_line}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-center">
                                                {primaryMastery ? (
                                                    <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-cyan-950/55">
                                                        <ChampionIconWithFallback
                                                            className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                            src={buildSearchChampionIconSrc(primaryMastery.name_id)}
                                                            alt={`${primaryMastery.name} icon`}
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="shrink-0 text-right">
                                                {isRanked ? (
                                                    <>
                                                        <img
                                                            className="ml-auto h-10 w-10 object-contain"
                                                            src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                            alt={`${result.competitive?.tier} tier emblem`}
                                                        />
                                                        <div className="mt-0.5 text-[11px] text-zinc-300">
                                                            {result.competitive.league_points} LP
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                        Unranked
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden min-w-0 items-center gap-3 md:flex md:w-[228px]">
                                        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg border border-zinc-700">
                                            <ProfileIconWithFallback
                                                className="block h-full w-full object-cover"
                                                src={buildSearchProfileIconSrc(result.profile_icon_id)}
                                                alt="summoner profile icon"
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex min-w-0 items-center gap-1.5">
                                                <span className="truncate text-sm font-semibold text-zinc-100">
                                                    {result.game_name}
                                                </span>
                                                {result.preferred_role ? (
                                                    <img
                                                        alt={`${result.preferred_role} role icon`}
                                                        className="h-3.5 w-3.5 shrink-0 opacity-90"
                                                        src={buildSearchRoleIconSrc(result.preferred_role)}
                                                    />
                                                ) : null}
                                            </div>
                                            <div className="mt-0.5 truncate text-[11px] uppercase tracking-[0.12em] text-zinc-400">
                                                #{result.tag_line}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden min-w-0 md:block md:w-[300px]">
                                        {masteryEntries.length ? (
                                            <div className="flex flex-wrap items-center gap-1.5 md:max-w-[300px]">
                                                {masteryEntries.map((mastery) => (
                                                    <div
                                                        key={`${result.puuid}-${mastery.champion_id}`}
                                                        className="inline-flex items-center"
                                                    >
                                                        <div className="h-9 w-9 overflow-hidden rounded-full border-2 border-cyan-950/55">
                                                            <ChampionIconWithFallback
                                                                className="h-full w-full scale-[1.18] rounded-full object-cover"
                                                                src={buildSearchChampionIconSrc(mastery.name_id)}
                                                                alt={`${mastery.name} icon`}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-[11px] text-zinc-500 md:text-left">
                                                No mastery snapshot cached
                                            </div>
                                        )}
                                    </div>

                                    <div className="hidden shrink-0 md:block md:w-[136px] md:justify-self-end">
                                        {isRanked ? (
                                            <div className="flex items-center justify-end gap-2">
                                                <div className="min-w-0 text-right">
                                                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-100">
                                                        {eloLabel}
                                                    </div>
                                                    <div className="mt-0.5 text-[11px] text-zinc-300">
                                                        {result.competitive.league_points} LP
                                                    </div>
                                                </div>
                                                <img
                                                    className="h-10 w-10 shrink-0 object-contain"
                                                    src={buildSearchTierIconSrc(result.competitive?.tier)}
                                                    alt={`${result.competitive?.tier} tier emblem`}
                                                />
                                            </div>
                                        ) : (
                                            <div className="text-right text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-300">
                                                Unranked
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div className="px-4 py-3 text-sm text-zinc-300">
                    {canSearchDirectly ? (
                        <>
                            <div>No matching player found for {trimmedQuery}.</div>
                            <div className="mt-1">Check the GameName#TagLine and selected region.</div>
                        </>
                    ) : (
                        <>
                            <div>No matching results for {trimmedQuery}.</div>
                            <div className="mt-1">Try a different champion or player name, or enter a full GameName#TagLine.</div>
                        </>
                    )}
                </div>
            )}

            {hasSelectedRegion ? (
                <div className="border-t border-cyan-950/70 bg-zinc-900 p-2">
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            className={`inline-flex min-h-[38px] flex-1 items-center justify-center gap-2 rounded-lg border px-3 text-xs font-semibold uppercase tracking-[0.16em] transition duration-150 ${
                                trimmedQuery
                                    ? 'border-zinc-700 bg-zinc-950 text-zinc-200 hover:border-zinc-600 hover:bg-zinc-800'
                                    : 'cursor-not-allowed border-zinc-800 bg-zinc-950/70 text-zinc-500'
                            }`}
                            disabled={!trimmedQuery}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={onViewAllResults}
                        >
                            View Matching Summoners
                        </button>
                        {canSearchDirectly && !hasExactCachedMatch ? (
                            <button
                                type="button"
                                className="inline-flex min-h-[38px] flex-1 items-center justify-center rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-300 transition duration-150 hover:border-zinc-600 hover:bg-zinc-900 hover:text-zinc-100"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={onSubmitDirectSearch}
                            >
                                Search Riot Profile
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );

    if (!portalStyle || typeof document === 'undefined') {
        return null;
    }

    return createPortal(dropdownContent, document.body);
};

export default SearchSuggestionDropdown;
